// @ts-check

import { endpoints } from "@/utils/common/requests/endpoints";
import { genericQuery } from "../utils/genericQuery";

/**
 * @template T
 */
export class Query {
	/** @type {string[]} */
	key = [];

	/** @type {(args?: T) => Promise<import("../utils/genericQuery").GenericQueryResponse>} */
	#fnc = null;

	/**
	 * @param {{key: string[], fnc: (args?: T) => Promise<import("../utils/genericQuery").GenericQueryResponse>}} params
	 */
	constructor({ key, fnc }) {
		this.key = key;
		this.#fnc = fnc;
	}

	/**
	 * @param {T} [args]
	 * @returns {Promise<import("../utils/genericQuery").GenericQueryResponse>}
	 */
	run(args = /** @type {T} */ ({})) {
		return this.#fnc(args);
	}
}

export const crewQueries = {
	currentCrew: {
		key: ["crew", "current"],
		fnc: async () => {
			return await genericQuery({
				url: endpoints.crew.GET_CURRENT(),
			});
		},
	},
	getMembers: {
		key: ["crew", "members"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.GET_MEMBERS(args.id),
			});
		},
	},
	getInvitation: {
		key: ["crew", "invitation"],
		/**
		 * @param {{ invitationId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.GET_INVITATION(args.invitationId),
			});
		},
	},
	acceptInvitation: {
		key: ["crew", "invitation", "accept"],
		/**
		 * @param {{ invitationId: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.ACCEPT_INVITATION(args.invitationId),
				method: "post",
				payload: args.data,
			});
		},
	},
	create: {
		key: ["crew", "create"],
		/**
		 * @param {{ data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.CREATE(),
				method: "post",
				payload: args.data,
			});
		},
	},
	updateName: {
		key: ["crew", "update", "name"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.UPDATE_NAME(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	updateMemberRole: {
		key: ["crew", "update", "member", "role"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.UPDATE_MEMBER_ROLE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	deleteMember: {
		key: ["crew", "delete", "member"],
		/**
		 * @param {{ crewId: string, userId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.DELETE_MEMBER(args),
				method: "delete",
			});
		},
	},
	renewInvitation: {
		key: ["crew", "invitation", "renew"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.RENEW_INVITATION(args.id),
				method: "post",
			});
		},
	},
	delete: {
		key: ["crew", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const listQueries = {
	getAll: {
		key: ["list", "all"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.GET_ALL(args.crewId),
			});
		},
	},
	create: {
		key: ["list", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	generate: {
		key: ["list", "generate"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.GENERATE(args.crewId),
				method: "post",
			});
		},
	},
	delete: {
		key: ["list", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.DELETE(args.id),
				method: "delete",
			});
		},
	},
	clear: {
		key: ["list", "clear"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.CLEAR(args.crewId),
				method: "delete",
			});
		},
	},
};

export const categoryQueries = {
	getAll: {
		key: ["category", "all"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.GET_ALL(args.crewId),
			});
		},
	},
	create: {
		key: ["category", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	update: {
		key: ["category", "update"],
		/**
		 * @param {{ id: string, data: Record<string, unknown> }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.UPDATE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["category", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const itemQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseItem", "getAll"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItem.GET_ALL(args.crewId),
			});
		},
	}),
	create: {
		key: ["houseItem", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	update: {
		key: ["houseItem", "update"],
		/**
		 * @param {{ crewId: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.UPDATE(),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["houseItem", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.DELETE(args.id),
				method: "delete",
			});
		},
	},

	/** @type {Query<{crewId: string}>} */
	getNext: new Query({
		key: ["houseItem", "next"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItem.GET_NEXT(args.crewId),
			});
		},
	}),

	// Response shape: bare `{ averageDays: number | null }` — no `data` envelope.
	/** @type {Query<{id: string, crewId: string}>} */
	getPurchaseAverage: new Query({
		key: ["houseItem", "purchaseAverage"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItem.GET_PURCHASE_AVERAGE(args.id, args.crewId),
			});
		},
	}),
};

export const recipeQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["recipe", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.recipe.GET_ALL(args.crewId),
			});
		},
	}),

	/** @type {Query<{id: string}>} */
	getById: new Query({
		key: ["recipe", "id"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.recipe.GET_BY_ID(args.id),
			});
		},
	}),

	/** @type {Query<{shareToken: string}>} */
	getShared: new Query({
		key: ["recipe", "shared"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.recipe.GET_SHARED(args.shareToken),
			});
		},
	}),
};

export const itemCategoriesQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseItemCategory", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItemCategory.GET_ALL(args.crewId),
			});
		},
	}),
	create: {
		key: ["houseItemCategory", "create"],
		/**
		 * @param {{ crewId: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.CREATE(args.crewId),
				method: "post",
				payload: args.data,
			});
		},
	},
	update: {
		key: ["houseItemCategory", "update"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.UPDATE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["houseItemCategory", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const houseTaskQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseTask", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseTask.GET_ALL(args.crewId),
			});
		},
	}),
	/** @type {Query<{crewIds: string[]}>} */
	getAllCrews: new Query({
		key: ["houseTask", "allCrews"],
		fnc: async (args) => {
			const results = await Promise.all(
				args.crewIds.map((crewId) =>
					genericQuery({ url: endpoints.houseTask.GET_ALL(crewId) }),
				),
			);
			const allHouseTasks = results.flatMap((result, idx) => {
				const crewId = args.crewIds[idx];
				const houseTasks = /** @type {Record<string, unknown>[]} */ (
					result.houseTasks ?? []
				);
				return houseTasks.map((task) => ({ ...task, crewId }));
			});
			return { houseTasks: allHouseTasks };
		},
	}),
	/** @type {Query<{crewId: string}>} */
	getContributions: new Query({
		key: ["houseTask", "contributions"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseTask.GET_CONTRIBUTIONS(args.crewId),
			});
		},
	}),
	/** @type {Query<{crewId: string}>} */
	getAllContributions: new Query({
		key: ["houseTask", "contributions", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseTask.GET_ALL_CONTRIBUTIONS(args.crewId),
			});
		},
	}),
};

export const crewMembersQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["crewMembers", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.crew.GET_MEMBERS(args.crewId),
			});
		},
	}),
};

export const economosListQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["economosList", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosList.GET_ALL(args.crewId),
			});
		},
	}),
};

export const economosMovementQueries = {
	/** @type {Query<{listId: string, year: number, month: number}>} */
	getMonth: new Query({
		key: ["economosMovement", "getMonth"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_MONTH({
					listId: args.listId,
					year: args.year,
					month: args.month,
				}),
			});
		},
	}),

	/** @type {Query<{listId: string, year: number, month: number}>} */
	getWithFilter: new Query({
		key: ["economosMovement", "getWithFilter"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_WITH_FILTER({
					listId: args.listId,
					year: args.year,
					month: args.month,
				}),
			});
		},
	}),
	/** @type {Query<{listId: string}>} */
	getTags: new Query({
		key: ["economosMovement", "getTags"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_TAGS({
					listId: args.listId,
				}),
			});
		},
	}),
};

export const economosMetricsQueries = {
	/** @type {Query<{listId: string, year: number, month: number}>} */
	getMoneyMetrics: new Query({
		key: ["economosMetrics", "money"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMetrics.GET_MONEY_METRICS({
					listId: args.listId,
					year: args.year,
					month: args.month,
				}),
			});
		},
	}),
};

export const settingsQueries = {
	/** @type {Query<{}>} */
	get: new Query({
		key: ["settings", "get"],
		fnc: () => {
			return genericQuery({
				url: endpoints.settings.GET(),
			});
		},
	}),
};

export const splitExpenseGroupQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["splitExpenseGroup", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_ALL(args.crewId),
			});
		},
	}),
	/** @type {Query<{shortId: string}>} */
	getByShortId: new Query({
		key: ["splitExpenseGroup", "shortId"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_BY_SHORT_ID(args.shortId),
			});
		},
	}),
	/** @type {Query<{groupId: string}>} */
	getById: new Query({
		key: ["splitExpenseGroup", "id"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_BY_ID(args.groupId),
			});
		},
	}),
};

export const planQueries = {
	/** @type {Query<{crewId: string, date: string}>} */
	getUsage: new Query({
		key: ["plan", "usage"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.plan.GET_USAGE({ crewId: args.crewId, date: args.date }),
			});
		},
	}),
};

export const splitExpenseQueries = {
	/** @type {Query<{groupId: string}>} */
	getAll: new Query({
		key: ["splitExpense", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpense.GET_ALL(args.groupId),
			});
		},
	}),
};
